// src/components/utils/SentenceDisplay.js

import React, { useMemo } from 'react';
import { useFurigana } from '../../hooks/useFurigana';

const FURIGANA_ACTIVE_PADDING_TOP = 'pt-1';
const FURIGANA_CONTAINER_HEIGHT_CLASSES = 'max-h-4';

const WordToken = ({ word, isLearned, onClick, currentTime = 0, isKaraokeMode = false }) => {
  const [showFurigana] = useFurigana();
  const hasFurigana = word.sentence_form !== word.hiragana;
  
  // Calculate fill percentage for progressive highlighting
  let fillPercentage = 0;
  if (isKaraokeMode && word.timestamp_start !== undefined && word.timestamp_end !== undefined) {
    if (currentTime < word.timestamp_start) {
      fillPercentage = 0;
    } else if (currentTime > word.timestamp_end) {
      fillPercentage = 100;
    } else {
      const wordDuration = word.timestamp_end - word.timestamp_start;
      const elapsed = currentTime - word.timestamp_start;
      fillPercentage = Math.min(100, Math.max(0, (elapsed / wordDuration) * 100));
    }
  }
  
  const baseClasses =
    'inline-block align-bottom p-1.5 sm:p-3 rounded-xl shadow-md cursor-pointer transition-all duration-200 ease-in-out transform hover:-translate-y-1 focus:outline-none active:outline-none appearance-none';
  
  const stateClasses = isLearned
    ? 'bg-green-100 dark:bg-green-900/50 border-2 border-green-400 dark:border-green-600 hover:border-green-500'
    : 'bg-background-secondary border-2 border-border hover:border-primary';

  return (
    <button onClick={onClick} className={`${baseClasses} ${stateClasses}`}>
      <div className="flex flex-col items-center justify-end h-full">
        <div
          className={`text-xs sm:text-sm font-normal text-content-secondary transition-all duration-225 ease-in-out overflow-hidden
                                ${showFurigana && hasFurigana ? `${FURIGANA_CONTAINER_HEIGHT_CLASSES} opacity-100` : 'max-h-0 opacity-0'}
                                flex items-end`}
        >
          <span className="leading-tight">{word.hiragana}</span>
        </div>
        <span
          className={`text-xl sm:text-3xl font-bold leading-tight relative inline-block text-content-primary
                                  ${showFurigana && hasFurigana ? FURIGANA_ACTIVE_PADDING_TOP : ''}`}
          style={isKaraokeMode && fillPercentage > 0 && fillPercentage < 100 ? {
            backgroundImage: `linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary) ${fillPercentage}%, var(--color-content-primary) ${fillPercentage}%, var(--color-content-primary) 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textFillColor: 'transparent',
          } : isKaraokeMode && fillPercentage >= 100 ? {
            color: 'var(--color-primary)'
          } : {}}
        >
          {word.sentence_form}
        </span>
      </div>
    </button>
  );
};

const CompoundWordPart = ({ part, isLearned, onClick, isFirst, isLast, currentTime = 0, isKaraokeMode = false }) => {
  const [showFurigana] = useFurigana();
  const hasFurigana = part.sentence_form !== part.hiragana;
  
  // Calculate fill percentage for progressive highlighting
  let fillPercentage = 0;
  if (isKaraokeMode && part.timestamp_start !== undefined && part.timestamp_end !== undefined) {
    if (currentTime < part.timestamp_start) {
      fillPercentage = 0;
    } else if (currentTime > part.timestamp_end) {
      fillPercentage = 100;
    } else {
      const wordDuration = part.timestamp_end - part.timestamp_start;
      const elapsed = currentTime - part.timestamp_start;
      fillPercentage = Math.min(100, Math.max(0, (elapsed / wordDuration) * 100));
    }
  }
  
  const baseClasses =
    'relative flex flex-col items-center justify-end p-1.5 sm:p-3 shadow-md transition-all duration-200 ease-in-out transform hover:-translate-y-1 hover:z-10 focus:outline-none active:outline-none appearance-none';
  
  const bgClasses = isLearned ? 'bg-green-100 dark:bg-green-900/50' : 'bg-background-secondary';
  const borderColor = isLearned ? 'border-green-400' : 'border-border';

  const borderClasses = `
        border-t-2 border-b-2 ${borderColor}
        ${isFirst ? 'border-l-2' : ''}
        ${isLast ? 'border-r-2' : ''}
        hover:border-primary focus:border-primary
    `;
  const roundingClasses = `${isFirst ? 'rounded-l-lg' : ''} ${isLast ? 'rounded-r-lg' : ''}`;

  return (
    <button onClick={() => onClick(part)} className={`${baseClasses} ${bgClasses} ${roundingClasses} ${borderClasses}`}>
      <div className="flex flex-col items-center justify-end h-full">
        <div
          className={`text-xs sm:text-sm font-normal text-content-secondary transition-all duration-225 ease-in-out overflow-hidden
                                ${showFurigana && hasFurigana ? `${FURIGANA_CONTAINER_HEIGHT_CLASSES} opacity-100` : 'max-h-0 opacity-0'}
                                flex items-end`}
        >
          <span className="leading-tight">{part.hiragana}</span>
        </div>
        <span
          className={`text-lg sm:text-3xl font-bold leading-tight relative inline-block text-content-primary
                                  ${showFurigana && hasFurigana ? FURIGANA_ACTIVE_PADDING_TOP : ''}`}
          style={isKaraokeMode && fillPercentage > 0 && fillPercentage < 100 ? {
            backgroundImage: `linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary) ${fillPercentage}%, var(--color-content-primary) ${fillPercentage}%, var(--color-content-primary) 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textFillColor: 'transparent',
          } : isKaraokeMode && fillPercentage >= 100 ? {
            color: 'var(--color-primary)'
          } : {}}
        >
          {part.sentence_form}
        </span>
      </div>
    </button>
  );
};

const SentenceDisplay = ({ item, onItemClick, learnedWordIds = new Set(), currentTime = 0, isKaraokeMode = false }) => {
  const groupedWords = useMemo(() => {
    if (!item?.words) return [];
    const words = item.words;
    const result = [];
    let i = 0;
    while (i < words.length) {
      const word = words[i];
      if (word.compound_order === 0) {
        const compoundGroup = [word];
        let nextIndex = i + 1;
        while (nextIndex < words.length && words[nextIndex].compound_order === compoundGroup.length) {
          compoundGroup.push(words[nextIndex]);
          nextIndex++;
        }
        result.push(compoundGroup);
        i = nextIndex;
      } else {
        result.push(word);
        i++;
      }
    }
    return result;
  }, [item.words]);

  return (
    <div className="flex flex-wrap justify-start items-stretch gap-y-2 gap-x-1.5" lang="ja" data-tour="clickable-words">
      {groupedWords.map((wordOrGroup, index) => {
        if (Array.isArray(wordOrGroup)) {
          return (
            <div key={index} className="flex">
              {wordOrGroup.map((part, partIndex) => (
                <CompoundWordPart
                  key={part.id}
                  part={part}
                  isLearned={learnedWordIds.has(part.id)}
                  onClick={onItemClick}
                  isFirst={partIndex === 0}
                  isLast={partIndex === wordOrGroup.length - 1}
                  currentTime={currentTime}
                  isKaraokeMode={isKaraokeMode}
                />
              ))}
            </div>
          );
        }

        const singleWord = wordOrGroup;
        return (
          <WordToken
            key={singleWord.id}
            word={singleWord}
            isLearned={learnedWordIds.has(singleWord.id)}
            onClick={() => onItemClick({ ...singleWord })}
            currentTime={currentTime}
            isKaraokeMode={isKaraokeMode}
          />
        );
      })}
    </div>
  );
};

export default SentenceDisplay;
